import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Thermometer, ClipboardCheck, CalendarDays, Receipt, PackageSearch,
  GraduationCap, UsersRound, KeyRound, ArrowRight, Sparkles, Check,
  ShieldCheck, Bell, FileDown, Zap, Heart, Users,
  ChefHat, Coffee, Store, Warehouse, Truck, ShoppingBag,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { useSEO } from "@/lib/useSEO";

const TRIAL_DAYS = 14;
const MONTHLY = 29;
const YEARLY = 290;

// Statistics sourced from the UK Food Standards Agency (FSA). See citation line below the grid.
const STATS = [
  { value: "1 in 5", label: "UK takeaways score Food Hygiene Rating 3 or below" },
  { value: "39,000", label: "UK food businesses were awaiting their first FSA inspection in April 2024" },
  { value: "6,199", label: "formal enforcement actions for food hygiene in 2024/25 — up 5% year on year" },
  { value: "24%", label: "of UK food businesses fall short of the top 5-star FHRS rating" },
];

const MODULES = [
  { icon: Thermometer, title: "Temperature logs", desc: "Every fridge, freezer, hot-hold and cold-store — tick them off in seconds. Trend alerts when something drifts." },
  { icon: ClipboardCheck, title: "H&S audits", desc: "Bespoke audit templates for kitchens, shops and warehouses. PDF exports for the EHO inspector." },
  { icon: CalendarDays, title: "Events & schedules", desc: "Kit checklists, opening checks, deliveries, cleaning schedules — never turn up short." },
  { icon: Receipt, title: "Invoices", desc: "Branded PDFs, Stripe payment links, auto-paid status. Get paid faster." },
  { icon: PackageSearch, title: "Batch & expiry", desc: "Multi-ingredient batches with the earliest expiry surfaced — for kitchens, retailers and warehouses alike." },
  { icon: GraduationCap, title: "Training records", desc: "Track Level 2 / Level 3 certificates, allergen awareness, renewal dates and gap alerts." },
  { icon: UsersRound, title: "Staff records", desc: "Right-to-work, contracts and emergency contacts — owner-only access." },
  { icon: KeyRound, title: "Team & access", desc: "Owner, manager and staff roles. Grant access record-by-record." },
];

// New "Who it's for" section — explicit business-type coverage.
const USE_CASES = [
  { icon: ChefHat, title: "Kitchens & restaurants", desc: "Fridge checks, HACCP audits, allergen records." },
  { icon: Coffee, title: "Cafés & bakeries", desc: "Opening checks, batch labels, staff training." },
  { icon: Store, title: "Food shops & delis", desc: "Chiller logs, supplier docs, retail traceability." },
  { icon: Warehouse, title: "Warehouses & cold stores", desc: "Multi-zone temperature monitoring, batch expiry." },
  { icon: Truck, title: "Catering & event kits", desc: "Mobile checklists, dietary notes, delivery paperwork." },
  { icon: ShoppingBag, title: "Ecommerce food retailers", desc: "Pack-out records, dispatch temperatures, customer traceability." },
];

const FAQ = [
  {
    q: "I don't run a kitchen — is this for me?",
    a: "Yes. Hearth & Crate is built for anyone who sells, stores, prepares or ships food in the UK — cafés, delis, farm shops, catering companies, warehouses and cold stores, ecommerce food retailers, and dark kitchens included. If the FSA or EHO can inspect you, the same records apply.",
  },
  {
    q: "Is there really a free trial?",
    a: `Yes — 14 days, no card required at sign-up. After that it's £${MONTHLY}/month or £${YEARLY}/year. If you don't subscribe the workspace becomes read-only — your data stays, but you can't add new records until you pay.`,
  },
  {
    q: "What happens to my data if I cancel?",
    a: "You get a 30-day grace period to change your mind. Cancel that and we hard-delete every record on day 30 — no shadow copies, no marketing list. You can also export everything as JSON + CSV any time from Settings → Account.",
  },
  {
    q: "Can my team log in too?",
    a: "Yes — owner, manager and staff roles. Staff records (right-to-work, contracts) are owner-only by default; you can grant a specific manager access record-by-record.",
  },
  {
    q: "Will this pass an EHO inspection?",
    a: "It's designed for the FSA's Safer Food, Better Business framework and the equivalent guidance for retailers, wholesalers and warehouses. Every temperature reading, audit and batch is timestamped and exportable as a PDF on demand — inspectors love a tidy PDF.",
  },
  {
    q: "Do you work for a one-person operation AND for a multi-site business?",
    a: "Yep. Each business is its own workspace. Owners can invite as many team members as they need at no extra cost — pricing is per workspace, not per seat. Multi-site? Talk to us about a single-bill discount.",
  },
  {
    q: "What if I refer another food business?",
    a: "You both get one free month. There's no cap — refer ten businesses, get ten free months. Your code lives in Settings → Account.",
  },
];

const QUOTE = {
  body: "Built by an indie food business for indie food businesses. We got tired of clipboard audits, dropped paperwork on inspection day, and trying to remember which fridge needed checking. So we made the thing we wished existed — for kitchens, shops, warehouses and everyone in between.",
  author: "OBN Enterprise — Founders, Hearth & Crate",
};

function Stat({ value, label }) {
  return (
    <div className="border border-[#e5e2d9] bg-white p-6 rounded-[4px]" data-testid="marketing-stat">
      <div className="font-display text-4xl text-[#1b3b36] leading-none">{value}</div>
      <div className="text-xs text-[#57534e] mt-3 leading-relaxed">{label}</div>
    </div>
  );
}

function ModuleCard({ icon: Icon, title, desc }) {
  return (
    <div className="border border-[#e5e2d9] bg-white p-5 rounded-[4px] hover:border-[#1b3b36] transition-colors group">
      <div className="w-9 h-9 rounded-[3px] bg-[#efece6] text-[#1b3b36] flex items-center justify-center group-hover:bg-[#1b3b36] group-hover:text-white transition-colors">
        <Icon className="w-4 h-4" strokeWidth={1.6} />
      </div>
      <h3 className="font-display text-xl text-[#1b3b36] mt-4">{title}</h3>
      <p className="text-sm text-[#57534e] mt-2 leading-relaxed">{desc}</p>
    </div>
  );
}

function FaqItem({ q, a }) {
  return (
    <details className="group border-b border-[#e5e2d9] py-5" data-testid="marketing-faq-item">
      <summary className="cursor-pointer list-none flex items-start justify-between gap-4">
        <span className="font-display text-lg text-[#1b3b36]">{q}</span>
        <span className="text-[#8a8476] group-open:rotate-45 transition-transform text-xl leading-none mt-0.5">+</span>
      </summary>
      <p className="text-sm text-[#57534e] leading-relaxed mt-3">{a}</p>
    </details>
  );
}

export default function Marketing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);

  useSEO({
    title: "Hearth & Crate — FSA compliance software for UK food businesses",
    description: "Digital temperature logs, HACCP audits, batch expiry, staff training and invoicing — for kitchens, cafés, food shops, warehouses and ecommerce food retailers. Free 14-day trial, £29/mo.",
    canonical: "https://hearthpro.co.uk/",
  });

  // Auto-redirect logged-in users to the dashboard so `/` always feels right.
  useEffect(() => {
    if (user && user.email) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    api.get("/public/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#f7f5f0] text-[#1c1917]" data-testid="marketing-page">
      {/* Nav */}
      <header className="border-b border-[#e5e2d9] bg-white/80 backdrop-blur supports-backdrop-blur:bg-white/60 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="font-display text-xl font-medium tracking-tight" data-testid="marketing-logo">
            Hearth<span className="text-[#c0612f]">.</span>
          </Link>
          <nav className="hidden md:flex items-center gap-7 text-xs uppercase tracking-[0.22em] text-[#57534e]">
            <a href="#who-its-for" className="hover:text-[#1b3b36]" data-testid="marketing-nav-who">Who it's for</a>
            <a href="#features" className="hover:text-[#1b3b36]" data-testid="marketing-nav-features">Features</a>
            <a href="#stats" className="hover:text-[#1b3b36]" data-testid="marketing-nav-stats">Why it matters</a>
            <Link to="/pricing" className="hover:text-[#1b3b36]" data-testid="marketing-nav-pricing">Pricing</Link>
            <a href="#faq" className="hover:text-[#1b3b36]" data-testid="marketing-nav-faq">FAQ</a>
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/login" data-testid="marketing-signin" className="text-sm text-[#1b3b36] hover:underline underline-offset-4">Sign in</Link>
            <Link to="/signup" data-testid="marketing-signup-top" className="text-xs uppercase tracking-[0.18em] bg-[#1b3b36] hover:bg-[#2c5a53] text-white px-4 py-2.5 rounded-[3px] transition-colors">
              Start free trial
            </Link>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-[#e5e2d9]">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 py-20 lg:py-28 grid lg:grid-cols-[1.05fr_0.95fr] gap-10 lg:gap-16 items-center">
          <div>
            <div className="text-[11px] uppercase tracking-[0.32em] text-[#c0612f] mb-5">For food businesses of every shape</div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.95] text-[#1b3b36]">
              Run your<br />food business<br />
              <span className="text-[#4e7b5b] italic">without the spreadsheets.</span>
            </h1>
            <p className="mt-7 text-base text-[#57534e] leading-relaxed max-w-lg">
              Temperature logs, H&S audits, batch traceability, invoices and staff records — one tidy place for <b>kitchens, cafés, food shops, warehouses and ecommerce retailers</b>. Audit-ready in <b>90 seconds</b>.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <Link to="/signup" data-testid="marketing-hero-cta" className="inline-flex items-center gap-2 bg-[#1b3b36] hover:bg-[#2c5a53] text-white font-medium px-6 py-3.5 rounded-[3px] tracking-wide transition-colors">
                Start your free {TRIAL_DAYS}-day trial
                <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
              </Link>
              <Link to="/pricing" data-testid="marketing-hero-pricing" className="inline-flex items-center gap-1.5 text-sm text-[#1b3b36] underline underline-offset-4 hover:text-[#c0612f]">
                See pricing — from £{MONTHLY}/mo
              </Link>
            </div>
            <div className="mt-6 flex items-center gap-4 text-xs text-[#8a8476]">
              <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-[#4e7b5b]" strokeWidth={2} /> No card required</span>
              <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-[#4e7b5b]" strokeWidth={2} /> Export your data anytime</span>
              <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-[#4e7b5b]" strokeWidth={2} /> UK-based support</span>
            </div>

            {stats && stats.active_workspaces >= 3 && (
              <div
                data-testid="marketing-social-proof"
                className="mt-6 inline-flex items-center gap-2.5 bg-white border border-[#e5e2d9] rounded-full px-4 py-2 text-sm text-[#57534e] shadow-sm"
              >
                <Users className="w-3.5 h-3.5 text-[#4e7b5b]" strokeWidth={2} />
                <span>
                  <b className="text-[#1b3b36]">{stats.active_workspaces}</b> paying UK food business{stats.active_workspaces === 1 ? "" : "es"} on board
                  {stats.joined_this_week > 0 && (
                    <>
                      <span className="mx-2 text-[#c8c2b6]">·</span>
                      <b className="text-[#c0612f]">+{stats.joined_this_week}</b> this week
                    </>
                  )}
                </span>
              </div>
            )}
          </div>
          <div className="relative">
            <div className="absolute -inset-3 bg-[#4e7b5b]/10 rounded-[6px] rotate-1" aria-hidden="true" />
            <img
              src="https://images.pexels.com/photos/4253133/pexels-photo-4253133.jpeg?auto=compress&cs=tinysrgb&w=900&dpr=2"
              alt="Chef preparing food in a tidy commercial kitchen"
              className="relative rounded-[4px] shadow-xl w-full object-cover aspect-[4/5] sm:aspect-[5/6] grayscale-[0.1]"
              data-testid="marketing-hero-image"
            />
            <div className="absolute -bottom-5 -left-5 bg-white border border-[#e5e2d9] rounded-[4px] p-4 shadow-md max-w-[230px] hidden sm:block">
              <div className="text-[10px] uppercase tracking-[0.24em] text-[#8a8476]">Today's checks</div>
              <div className="mt-2 space-y-2 text-sm">
                <div className="flex items-center justify-between"><span>Walk-in fridge</span><span className="text-[#4e7b5b] font-medium">3.2°C ✓</span></div>
                <div className="flex items-center justify-between"><span>Display chiller</span><span className="text-[#4e7b5b] font-medium">5.1°C ✓</span></div>
                <div className="flex items-center justify-between"><span>Freezer A</span><span className="text-[#4e7b5b] font-medium">-19°C ✓</span></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FOUNDING CUSTOMER — honest, real, one business */}
      <section className="border-b border-[#e5e2d9] bg-white" data-testid="marketing-founding-customer">
        <div className="max-w-4xl mx-auto px-6 sm:px-10 py-14 lg:py-18">
          <div className="text-[10px] uppercase tracking-[0.32em] text-[#8a8476] text-center">Founding customer</div>

          <div className="mt-6 flex flex-col items-center text-center">
            <div className="font-display text-3xl sm:text-4xl text-[#1b3b36] tracking-tight" data-testid="marketing-founding-name">
              OBN Panda Sweets and Snacks
            </div>
            <div className="mt-1 text-[11px] uppercase tracking-[0.22em] text-[#8a8476]">
              Food shop &amp; online retailer · UK
            </div>

            {/* 5-star hygiene badge — the actual, tangible proof */}
            <div className="mt-7 inline-flex flex-col items-center gap-2 bg-[#1b3b36] text-white rounded-[3px] px-8 py-5" data-testid="marketing-founding-rating">
              <div className="text-[10px] uppercase tracking-[0.32em] text-[#a8c4b8]">Food Hygiene Rating</div>
              <div className="flex items-center gap-1 mt-1" aria-label="5 out of 5 stars">
                {[0, 1, 2, 3, 4].map((i) => (
                  <svg key={i} viewBox="0 0 20 20" fill="#f7c948" width="22" height="22" aria-hidden="true">
                    <path d="M10 1.5l2.6 5.6 6 .7-4.4 4.2 1.1 6-5.3-3-5.3 3 1.1-6L1.4 7.8l6-.7L10 1.5z" />
                  </svg>
                ))}
              </div>
              <div className="text-2xl font-display mt-1">5 / 5</div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-[#a8c4b8] mt-1">Awarded with Hearth &amp; Crate</div>
            </div>

            <blockquote className="mt-8 max-w-xl text-[15px] leading-relaxed text-[#1c1917] italic">
              &ldquo;We passed our FSA inspection with a top 5-star hygiene rating — every record the inspector asked for was in Hearth, on my phone, in seconds.&rdquo;
            </blockquote>
            <div className="mt-3 text-[11px] uppercase tracking-[0.22em] text-[#8a8476]">
              — Founder, OBN Panda Sweets and Snacks
            </div>

            <div className="mt-8 flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#4e7b5b]">
              <ShieldCheck className="w-3.5 h-3.5" strokeWidth={2} />
              <span>Live user · UK-based · FSA 5-star verified</span>
            </div>
          </div>
        </div>
      </section>

      {/* WHO IT'S FOR — explicit business-type coverage */}
      <section id="who-its-for" className="border-b border-[#e5e2d9] bg-[#efece6]/40">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 py-20 lg:py-24">
          <div className="max-w-2xl">
            <div className="text-[11px] uppercase tracking-[0.32em] text-[#c0612f]">Who it's for</div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#1b3b36] mt-3 leading-tight">
              If it's food, and it's regulated,<br /><span className="italic text-[#4e7b5b]">it belongs here.</span>
            </h2>
            <p className="text-sm text-[#57534e] mt-4 leading-relaxed max-w-lg">
              Whether you're a one-person café, a farm shop, a cold-store warehouse or an ecommerce food brand — the same FSA rules apply. Hearth & Crate covers all of it, from one workspace.
            </p>
          </div>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="marketing-use-cases">
            {USE_CASES.map(({ icon: Icon, title, desc }) => (
              <div key={title} data-testid="marketing-use-case" className="bg-white border border-[#e5e2d9] rounded-[4px] p-6 hover:border-[#1b3b36] transition-colors group">
                <div className="w-10 h-10 rounded-[3px] bg-[#1b3b36]/8 text-[#1b3b36] flex items-center justify-center group-hover:bg-[#1b3b36] group-hover:text-white transition-colors">
                  <Icon className="w-5 h-5" strokeWidth={1.6} />
                </div>
                <h3 className="font-display text-lg text-[#1b3b36] mt-4">{title}</h3>
                <p className="text-sm text-[#57534e] mt-2 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* STATS */}
      <section id="stats" className="border-b border-[#e5e2d9] bg-[#efece6]/40">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 py-16 lg:py-20">
          <div className="text-[11px] uppercase tracking-[0.32em] text-[#c0612f]">Why it matters</div>
          <h2 className="font-display text-3xl sm:text-4xl tracking-tight text-[#1b3b36] mt-3 max-w-2xl leading-tight">
            Most inspections aren't lost to bad food.<br />They're lost to <span className="italic text-[#a23a30]">missing paperwork.</span>
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
            {STATS.map((s) => <Stat key={s.label} {...s} />)}
          </div>
          <p className="text-xs text-[#8a8476] mt-4 max-w-3xl leading-relaxed">
            Sources: FSA Annual Local Authority Performance Report 2024/25 (food.gov.uk); FSA Food Hygiene Rating Scheme Audit of Display and Business Survey 2024 (science.food.gov.uk); FSA FHRS national data 2023. Figures cover England, Wales and Northern Ireland.
          </p>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="border-b border-[#e5e2d9]">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 py-20">
          <div className="text-[11px] uppercase tracking-[0.32em] text-[#c0612f]">What's inside</div>
          <h2 className="font-display text-3xl sm:text-4xl tracking-tight text-[#1b3b36] mt-3 max-w-2xl leading-tight">
            Everything your inspector wants to see.<br />Nothing they don't.
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
            {MODULES.map((m) => <ModuleCard key={m.title} {...m} />)}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="border-b border-[#e5e2d9] bg-[#1b3b36] text-white">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 py-20">
          <div className="text-[11px] uppercase tracking-[0.32em] text-[#e9c4a3]">How it works</div>
          <h2 className="font-display text-3xl sm:text-4xl tracking-tight text-white mt-3 max-w-2xl leading-tight">
            Three days from sign-up to <span className="italic text-[#e9c4a3]">audit-ready.</span>
          </h2>
          <div className="grid md:grid-cols-3 gap-8 mt-12">
            <div>
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#e9c4a3]"><Zap className="w-3 h-3" /> Day 1</div>
              <h3 className="font-display text-2xl text-white mt-3">Set up your kitchen</h3>
              <p className="text-sm text-white/70 mt-2 leading-relaxed">Add your fridges, your audit areas, your staff. Templates seeded for you — tweak to suit.</p>
            </div>
            <div>
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#e9c4a3]"><Bell className="w-3 h-3" /> Day 2</div>
              <h3 className="font-display text-2xl text-white mt-3">Daily checks in 60 seconds</h3>
              <p className="text-sm text-white/70 mt-2 leading-relaxed">Staff tap through temperature checks, cleaning rotas and prep batches. Owner gets an emailed digest at 8am.</p>
            </div>
            <div>
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#e9c4a3]"><FileDown className="w-3 h-3" /> Day 3</div>
              <h3 className="font-display text-2xl text-white mt-3">Export anything, instantly</h3>
              <p className="text-sm text-white/70 mt-2 leading-relaxed">Inspector at the door? One click → a clean PDF of every check, audit and certificate. Done.</p>
            </div>
          </div>
        </div>
      </section>

      {/* QUOTE */}
      <section className="border-b border-[#e5e2d9]">
        <div className="max-w-3xl mx-auto px-6 sm:px-10 py-20 text-center">
          <Heart className="w-5 h-5 text-[#c0612f] mx-auto" strokeWidth={1.6} />
          <blockquote className="font-display text-2xl sm:text-3xl text-[#1b3b36] mt-5 leading-snug">
            “{QUOTE.body}”
          </blockquote>
          <div className="text-xs uppercase tracking-[0.28em] text-[#8a8476] mt-6">{QUOTE.author}</div>
        </div>
      </section>

      {/* PRICING TEASER */}
      <section id="pricing" className="border-b border-[#e5e2d9] bg-[#efece6]/40">
        <div className="max-w-5xl mx-auto px-6 sm:px-10 py-20">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white border border-[#e5e2d9] rounded-[4px] p-7">
              <div className="text-[10px] uppercase tracking-[0.24em] text-[#8a8476]">Monthly</div>
              <div className="flex items-baseline gap-1.5 mt-3">
                <span className="font-display text-5xl text-[#1b3b36]">£{MONTHLY}</span>
                <span className="text-sm text-[#8a8476]">/month</span>
              </div>
              <p className="text-sm text-[#57534e] mt-3">Cancel anytime. {TRIAL_DAYS}-day free trial.</p>
            </div>
            <div className="relative bg-white border-2 border-[#1b3b36] rounded-[4px] p-7">
              <span className="absolute -top-2.5 right-5 text-[9px] uppercase tracking-[0.24em] bg-[#c0612f] text-white px-2.5 py-1 rounded-[3px]">Best value</span>
              <div className="text-[10px] uppercase tracking-[0.24em] text-[#8a8476]">Yearly</div>
              <div className="flex items-baseline gap-1.5 mt-3">
                <span className="font-display text-5xl text-[#1b3b36]">£{YEARLY}</span>
                <span className="text-sm text-[#8a8476]">/year</span>
              </div>
              <p className="text-sm text-[#57534e] mt-3">Two months free. Works out at £{(YEARLY / 12).toFixed(2)}/month.</p>
            </div>
          </div>
          <div className="mt-8 text-center">
            <Link to="/pricing" data-testid="marketing-see-pricing" className="inline-flex items-center gap-1.5 text-sm text-[#1b3b36] underline underline-offset-4 hover:text-[#c0612f]">
              See the full feature comparison
              <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="border-b border-[#e5e2d9]">
        <div className="max-w-3xl mx-auto px-6 sm:px-10 py-20">
          <div className="text-[11px] uppercase tracking-[0.32em] text-[#c0612f]">Common questions</div>
          <h2 className="font-display text-3xl sm:text-4xl tracking-tight text-[#1b3b36] mt-3">Things worth knowing.</h2>
          <div className="mt-8">
            {FAQ.map((f) => <FaqItem key={f.q} {...f} />)}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-[#fff7ec]">
        <div className="max-w-3xl mx-auto px-6 sm:px-10 py-20 text-center">
          <Sparkles className="w-6 h-6 text-[#c0612f] mx-auto" strokeWidth={1.6} />
          <h2 className="font-display text-4xl sm:text-5xl tracking-tight text-[#7a3d12] mt-4 leading-tight">
            Ready to bin the clipboards?
          </h2>
          <p className="text-sm text-[#7a3d12]/80 mt-4 leading-relaxed">14 days free. No card. Cancel any time. We'll even import last month's logs from your spreadsheet if you ask nicely.</p>
          <Link to="/signup" data-testid="marketing-final-cta" className="inline-flex items-center gap-2 mt-8 bg-[#1b3b36] hover:bg-[#2c5a53] text-white font-medium px-7 py-4 rounded-[3px] tracking-wide transition-colors">
            Start free {TRIAL_DAYS}-day trial
            <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
          </Link>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-[#e5e2d9] bg-white">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 py-10 flex flex-wrap items-center justify-between gap-4 text-xs text-[#8a8476]">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-[#4e7b5b]" strokeWidth={1.8} />
            <span>UK-based. GDPR-compliant. <Link to="/legal/privacy" className="underline">Privacy</Link>.</span>
          </div>
          <div className="flex items-center gap-5">
            <Link to="/legal/terms" className="hover:text-[#1b3b36]">Terms</Link>
            <Link to="/legal/privacy" className="hover:text-[#1b3b36]">Privacy</Link>
            <Link to="/legal/cookies" className="hover:text-[#1b3b36]">Cookies</Link>
            <Link to="/pricing" className="hover:text-[#1b3b36]">Pricing</Link>
            <Link to="/login" className="hover:text-[#1b3b36]">Sign in</Link>
          </div>
          <div>© {new Date().getFullYear()} OBN Enterprise Ltd.</div>
        </div>
      </footer>
    </div>
  );
}
